import type { QueryView } from '@krp/client-runtime'

import { AQSI_RIGHTS } from '../../../shared/model/rights'

/**
 * Диагностика продаж (RP-SALE-TRACE): ответы службы и модель экрана.
 * Здесь только то, что сервер отдаёт по договору `trace/*`; персональных данных в нём нет — идентификаторы и времена.
 */

/** Право кабинета, по которому читается трассировка и включается подробный режим. */
export const TRACE_CAPABILITY = AQSI_RIGHTS.salesTrace

/** Откуда взята контрольная точка. */
export type TraceSource = 'browser' | 'server' | 'cabinet' | 'register'

/** Участок времени продажи; ключи — договор сервера. */
export const TRACE_SEGMENT_KEYS = ['browser', 'network', 'server', 'cabinet-to-register', 'terminal', 'register-receipt'] as const
export type TraceSegmentKey = (typeof TRACE_SEGMENT_KEYS)[number]

export interface TraceSegment {
  /** Ключ участка; неизвестный ключ допустим (служба новее клиента) — он рисуется нейтрально. */
  key: string
  /** Название от службы, показывается, если клиент ключа не знает. */
  label: string
  ms: number
}

export interface AqsiTraceSaleItem {
  saleId: string
  startedAt: string
  finishedAt: string | null
  outcome: string
  totalMs: number
  dominant: string
  segments: readonly TraceSegment[]
}

export interface AqsiTraceSales {
  items: readonly AqsiTraceSaleItem[]
}

export interface AqsiTracePoint {
  source: TraceSource
  stage: string
  at: string
  durationMs: number | null
  outcome: string | null
}

export interface AqsiTraceNetwork {
  effectiveType: string | null
  rttMs: number | null
  downlinkMbps: number | null
}

/** Настройки кассы на момент продажи — те, что могут влиять на время чека. */
export interface AqsiTraceRegisterSettings {
  acquiringSlipsCount: number | null
  receiptCompactCheck: boolean | null
  hasAdvertisingLines: boolean | null
  acquiringLockFn: boolean | null
  isStandbyModeEnabled: boolean | null
  displayTurningOffTimeoutMillis: number | null
  modeAcquiringTerminalEnable: boolean | null
  isCardQrCombined: boolean | null
}

/** Событие подробного режима, как его хранит служба. */
export interface AqsiTraceDetailEvent {
  kind: string
  route: string
  method: string | null
  at: string
  durationMs: number | null
  status: number | null
  attempt: number | null
  bytes: number | null
}

export interface AqsiTraceSale {
  saleId: string
  points: readonly AqsiTracePoint[]
  segments: readonly TraceSegment[]
  dominant: string
  network: AqsiTraceNetwork | null
  registerSettings: AqsiTraceRegisterSettings | null
  detail: readonly AqsiTraceDetailEvent[]
}

export interface AqsiTraceDeepState {
  enabled: boolean
  /** До какого момента подробный режим включён; `null` — выключен. */
  until: string | null
}

export interface AqsiRegisterCheckRun {
  deliveryMs: number | null
  totalMs: number | null
  outcome: string
}

export interface AqsiRegisterCheck {
  runs: readonly AqsiRegisterCheckRun[]
  medianDeliveryMs: number | null
}

// --- модель экрана ------------------------------------------------------------------------

export interface TraceSaleRowVm {
  saleId: string
  startedAt: string
  /** «сегодня в 14:42» */
  startedText: string
  outcome: string
  totalText: string
  dominantText: string
  selected: boolean
}

export interface TraceWaterfallRowVm {
  key: string
  label: string
  /** «1,2 с» */
  durationText: string
  /** «18 %» */
  shareText: string
  /** Смещение начала участка от начала продажи, 0…100. */
  offsetPercent: number
  /** Длина участка, 0…100 (не меньше заметной полоски). */
  widthPercent: number
  /** Участок, на который ушло больше всего времени. */
  dominant: boolean
}

export interface TracePointRowVm {
  id: string
  sourceText: string
  stageText: string
  /** Смещение от первой точки: «+0,12 с». */
  offsetText: string
  durationText: string
  outcome: string
}

export interface TraceSettingRowVm {
  key: string
  label: string
  valueText: string
  /** Пометка «может замедлять чек: …»; `null` — настройка на время не влияет или значение не известно. */
  slowsNote: string | null
}

export interface TraceSaleVm {
  saleId: string
  query: QueryView
  totalText: string
  /** «Больше всего времени ушло на: наш сервер — 0,4 с (6 %)». `null` — разбора нет. */
  summaryText: string | null
  waterfall: readonly TraceWaterfallRowVm[]
  points: readonly TracePointRowVm[]
  networkText: string | null
  settings: readonly TraceSettingRowVm[]
  /** Сколько событий подробного режима записано по продаже. */
  detailCount: number
  /** Текст для «Скопировать отчёт»; `null`, пока разбора нет. */
  report: string | null
}

export interface TraceTrendVm {
  /** Точки «кабинет → касса» по времени, мс; не менее пяти. */
  values: readonly number[]
  medianText: string
  maxText: string
}

/** Ошибка команды: текст службы дословно и служебная строка с кодом. */
export interface TraceFailureVm {
  message: string
  meta: string
}

export interface TraceDeepVm {
  /** Состояние прочитано с сервера. */
  known: boolean
  enabled: boolean
  /** «Подробный режим включён до 17:42 — выключится сам» / «Подробный режим выключен». */
  text: string
  busy: boolean
  error: TraceFailureVm | null
}

export interface TraceCheckRunVm {
  id: string
  text: string
}

export interface TraceCheckVm {
  busy: boolean
  error: TraceFailureVm | null
  /** Строки последнего замера; пусто — замера в этой сессии не было. */
  runs: readonly TraceCheckRunVm[]
  /** «Медиана доставки: 0,8 с»; `null` — замера не было или доставка не измерена. */
  medianText: string | null
}

export interface TraceToolsVm {
  deep: TraceDeepVm
  check: TraceCheckVm
}

export interface TraceScreenVm {
  list: { query: QueryView; rows: readonly TraceSaleRowVm[] }
  trend: TraceTrendVm | null
  sale: TraceSaleVm | null
  tools: TraceToolsVm
}

export interface TraceScreenActions {
  setDeep: (enabled: boolean) => void
  /** Пять служебных чтений кассе; экран вызывает только после подтверждения человека. */
  runRegisterCheck: () => void
  select: (saleId: string) => void
  closeSale: () => void
  reloadList: () => void
  reloadSale: () => void
}
